import Phaser from "phaser";
import { PHASES, type PhaseId, type PhaseDef } from "../config/phases";
import { ACTIVE_DIFFICULTY } from "../config/difficultyConfig";

export interface WaveState {
  wave: number; // 1-based wave number
  phase: PhaseDef;
  isBoss: boolean;
  elapsed: number; // seconds since wave start
}

export class WaveManager {
  private state: WaveState;
  private get WAVE_DURATION() { return ACTIVE_DIFFICULTY.waveDuration; }

  constructor(_scene: Phaser.Scene, startWave = 1) {
    this.state = this.buildState(startWave);
  }

  private buildState(wave: number): WaveState {
    const phaseIdx = wave - 1;
    const clampedPhase = Math.min(phaseIdx, 15) as PhaseId;
    const phase = PHASES[clampedPhase];
    return { wave, phase, isBoss: false, elapsed: 0 };
  }

  public current(): WaveState {
    return this.state;
  }

  public bossesDefeated = 0;

  public currentPhase(): PhaseDef {
    return this.state.phase;
  }

  /**
   * Difficulty multiplier — scaled primarily on bossesDefeated
   * so enemies don't infinitely outscale a struggling player trapped in a wave.
   */
  public difficulty(): number {
    const bossFactor = this.bossesDefeated * ACTIVE_DIFFICULTY.bossDifficultyFactor;
    const waveProgress = Math.max(0, (this.state.wave - 1 - this.bossesDefeated)) * ACTIVE_DIFFICULTY.waveDifficultyFactor;
    return 1 + bossFactor + waveProgress;
  }

  /** Effective enemy HP for this wave. */
  public enemyHp(): number {
    return Math.round(ACTIVE_DIFFICULTY.enemyBaseHp * this.state.phase.enemyHpMultiplier * this.difficulty());
  }

  /** Effective boss HP for this wave. */
  public bossHp(): number {
    return Math.round(ACTIVE_DIFFICULTY.bossBaseHp * this.state.phase.enemyHpMultiplier * this.difficulty());
  }

  /** Effective spawn interval (seconds) — shorter = faster spawns. */
  public spawnInterval(): number {
    return (1.0 * this.state.phase.spawnRateMultiplier) / this.difficulty();
  }

  /** Advance the internal clock. Returns true if the wave should transition to boss. */
  public tick(dtSec: number): boolean {
    if (this.state.isBoss) return false;
    this.state.elapsed += dtSec;
    if (this.state.elapsed >= this.WAVE_DURATION) {
      this.state.isBoss = true;
      return true;
    }
    return false;
  }

  /**
   * Move to the next wave.
   */
  public advance(): WaveState {
    this.state = this.buildState(this.state.wave + 1);
    return this.state;
  }

  /** Reset to wave 1 — used on game over → restart. */
  public reset(startWave = 1): void {
    this.bossesDefeated = 0;
    this.state = this.buildState(startWave);
  }
}
